import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Button from './Button';
import Pagination from './Pagination';
import useDebouncedValue from '../hooks/useDebouncedValue';

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function BlockList({ onError, title, endpoint, addPlaceholder = 'Value to block', emptyLabel = 'No block rules yet', multiline = false, addHint = '' }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [summary, setSummary] = useState(null);

  const debouncedQuery = useDebouncedValue(query);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, pageSize]);

  const fetchPage = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLoading(true);
    try {
      const res = await axios.get(endpoint, {
        params: { page, pageSize, q: debouncedQuery || undefined },
      });

      if (requestIdRef.current !== requestId) return;

      const data = res.data || {};
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
      if (data.page > data.totalPages) setPage(data.totalPages);
    } catch (err) {
      if (requestIdRef.current === requestId) onError(err);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [page, pageSize, debouncedQuery, endpoint, onError]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || busy) return;

    setBusy(true);
    try {
      const res = await axios.post(endpoint, { value: trimmed });
      setSummary(multiline ? res.data : null);
      setValue('');
      setQuery('');
      setPage(1);
      await fetchPage();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (rule) => {
    setPendingId(rule.id);
    try {
      await axios.patch(`${endpoint}/${rule.id}`, { enabled: !rule.enabled });
      await fetchPage();
    } catch (err) {
      onError(err);
    } finally {
      setPendingId(null);
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete block rule "${rule.value}"?`)) return;

    setPendingId(rule.id);
    try {
      await axios.delete(`${endpoint}/${rule.id}`);
      await fetchPage();
    } catch (err) {
      onError(err);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="app-editor-card compact-card settings-card">
      <div className="app-editor-card-body compact-card-body">
        <div className="settings-card-head">
          <div className="stats-section-title">{title}</div>
          <div className="dashboard-badge">{total}</div>
        </div>

        <div className="settings-toolbar">
          {multiline ? (
            <form className="bulk-add" onSubmit={submit}>
              <textarea
                className="form-control bulk-add-input"
                rows={4}
                placeholder={addPlaceholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              <div className="bulk-add-foot">
                {addHint ? <span className="bulk-add-hint">{addHint}</span> : null}
                <Button
                  type="submit"
                  variant="danger"
                  className="dashboard-btn flex-shrink-0"
                  disabled={busy || !value.trim()}
                >
                  {busy ? 'Додаємо…' : 'Додати'}
                </Button>
              </div>
              {summary ? (
                <div className="bulk-add-result">
                  Додано {summary.added}
                  {summary.duplicates ? `, вже було ${summary.duplicates}` : ''}
                  {summary.invalid?.length ? `, не розпізнано: ${summary.invalid.join(', ')}` : ''}
                </div>
              ) : null}
            </form>
          ) : (
            <form className="settings-add-row" onSubmit={submit}>
              <input
                type="text"
                className="form-control"
                placeholder={addPlaceholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              <Button
                type="submit"
                variant="danger"
                className="dashboard-btn flex-shrink-0"
                disabled={busy || !value.trim()}
              >
                Add
              </Button>
            </form>
          )}

          <input
            type="text"
            className="form-control settings-search"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="settings-list">
          {loading && items.length === 0 ? (
            <div className="settings-empty">Loading…</div>
          ) : items.length === 0 ? (
            <div className="settings-empty">
              {debouncedQuery ? 'Nothing found' : emptyLabel}
            </div>
          ) : (
            <div className="stats-table-wrap" style={{ opacity: loading ? 0.55 : 1 }}>
              <table className="rule-table">
                <thead>
                  <tr>
                    <th>Value</th>
                    <th style={{ width: '70px' }}>On</th>
                    <th className="text-end" style={{ width: '90px' }}>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((rule) => (
                    <tr key={rule.id}>
                      <td>
                        <div className="rule-value">{rule.value}</div>
                        <div className="rule-meta">{formatDateTime(rule.createdAt)}</div>
                      </td>
                      <td>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={rule.enabled}
                            disabled={pendingId === rule.id}
                            onChange={() => toggle(rule)}
                            aria-label={`Toggle ${rule.value}`}
                          />
                        </div>
                      </td>
                      <td className="text-end">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="dashboard-btn-sm"
                          disabled={pendingId === rule.id}
                          onClick={() => remove(rule)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="settings-card-foot">
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
}
